import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { ProfileLinksApiError } from './profile-links-error';
import {
  HANDLE_REFUSALS,
  type ClaimHandleResult,
  type HandleCheck,
  type HandleRefusal,
  type OpenProfileThreadResult,
  type PrefillCardResult,
  type ProfileLeads,
  type ProfileLink,
  type ProfileLinkPatch,
  type ProfileLinksList,
  type ProfileLinkStats,
} from './profile-links-types';

/**
 * THE client of Symphony Profile Links on web — every owner screen and the visitor's `/profile-thread` go
 * through this one module (docs/symphony-profile-links-design.md §5.5, §6.1, §8).
 *
 * Every call is a same-origin POST to `/api/profile-links/<op>` (`pages/api/profile-links/[op].ts`), which
 * checks `Sec-Fetch-Site`, authorizes from the session and forwards an allowlisted body to the native-data
 * bridge. Nothing here sends who the caller is: there is no metasite, user or owner id in any request — the
 * server resolves that from the session cookie. A link is named by its own id, a profile by its public
 * handle.
 *
 * The route proxies each op to the native-data action the phones post, which calls `ProfileLinkService`.
 * Every refusal the service makes reaches here as a `ProfileLinksApiError` carrying its code — `not_found`,
 * `forbidden` (the feature is not available to this owner), `content_policy`, `context_taken`, … — except
 * the two the screens render as answers: a handle `claim-handle` would not take (`ClaimHandleResult`), and an
 * open that was refused, normalised below. Tests and stories use `createFakeProfileLinksApi`
 * (lib/profile-links-fixtures.ts), which implements the same interface over fixtures.
 */
export interface ProfileLinksApi {
  list(): Promise<ProfileLinksList>;
  get(linkId: string): Promise<ProfileLink>;
  createDraft(input: { contextSlug?: string; contextTitle?: string }): Promise<ProfileLink>;
  update(linkId: string, patch: ProfileLinkPatch): Promise<ProfileLink>;
  /** Draft → live, and paused → live again. */
  publish(linkId: string): Promise<ProfileLink>;
  pause(linkId: string): Promise<ProfileLink>;
  archive(linkId: string): Promise<ProfileLink>;
  stats(linkId: string): Promise<ProfileLinkStats>;
  leads(linkId: string): Promise<ProfileLeads>;
  /** The VISITOR's call: open (or reuse) the profile thread for this link (§6.1 step 3). */
  openProfileThread(handle: string, context?: string): Promise<OpenProfileThreadResult>;
  checkHandle(handle: string): Promise<HandleCheck>;
  /** Claims the handle, and drafts the main profile's card from what Symphony knows (`language` = the app's). */
  claimHandle(handle: string, language?: string): Promise<ClaimHandleResult>;
  /** Fills the EMPTY fields of a link's DRAFT card from what Symphony knows. Never publishes. */
  /**
   * `auto`: the editor opened on a card with an empty field, rather than the owner pressing "Fill". The server
   * throttles it to once a day per link and never re-fills a field the owner cleared.
   */
  prefillCard(linkId: string, language?: string, options?: { auto?: boolean }): Promise<PrefillCardResult>;
}

type Fetcher = (input: string, init: RequestInit) => Promise<Response>;

async function call<T>(fetcher: Fetcher, op: string, body: Record<string, unknown>): Promise<T> {
  const res = await fetcher(`${API_BASE}/api/profile-links/${op}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let parsed: unknown = undefined;
  try {
    parsed = text ? JSON.parse(text) : {};
  } catch {
    parsed = undefined;
  }
  if (!res.ok) {
    const code = parsed && typeof (parsed as { error?: unknown }).error === 'string' ? (parsed as { error: string }).error : 'request_failed';
    throw new ProfileLinksApiError(code, res.status);
  }
  if (parsed === undefined) throw new ProfileLinksApiError('bad_response', res.status);
  return parsed as T;
}

const asHandleRefusal = (value: unknown): HandleRefusal => {
  const reason = typeof value === 'string' ? value.toLowerCase() : '';
  return (HANDLE_REFUSALS as readonly string[]).includes(reason) ? (reason as HandleRefusal) : 'taken';
};

/** The reason compared case-insensitively — the service spells it upper case, the copy keys lower. */
export function normalizeHandleCheck(raw: { available?: unknown; reason?: unknown }): HandleCheck {
  return raw.available === true ? { available: true } : { available: false, reason: asHandleRefusal(raw.reason) };
}

export function normalizeClaim(raw: { handle?: unknown; link?: unknown; refusal?: unknown }): ClaimHandleResult {
  if (raw.refusal !== undefined || typeof raw.handle !== 'string' || !raw.handle || !raw.link) return { refusal: asHandleRefusal(raw.refusal) };
  return { handle: raw.handle.toLowerCase(), link: raw.link as ProfileLink };
}

/**
 * The open's union, with `refused` turned into the error a screen already renders: `link_inactive` is the
 * same 404 as every other miss (never which links exist), `busy` / `blocked` a failure to open.
 */
export function normalizeOpen(raw: { kind?: unknown; reason?: unknown } & Record<string, unknown>): OpenProfileThreadResult {
  if (raw.kind === 'thread' && typeof raw.conversationId === 'string' && raw.conversationId) {
    return {
      kind: 'thread',
      conversationId: raw.conversationId,
      ownerName: typeof raw.ownerName === 'string' ? raw.ownerName : '',
      ...(typeof raw.contextTitle === 'string' && raw.contextTitle ? { contextTitle: raw.contextTitle } : {}),
    };
  }
  if (raw.kind === 'own_link' && typeof raw.linkId === 'string' && raw.linkId) return { kind: 'own_link', linkId: raw.linkId };
  if (raw.kind === 'refused') {
    const reason = typeof raw.reason === 'string' ? raw.reason.toLowerCase() : '';
    if (reason === 'busy' || reason === 'blocked') throw new ProfileLinksApiError(reason, 409);
    throw new ProfileLinksApiError('not_found', 404);
  }
  throw new ProfileLinksApiError('bad_response', 200);
}

/** Every single-link op answers `{ link }`; a 200 without one is not a link. */
async function linkOf(response: Promise<{ link?: ProfileLink }>): Promise<ProfileLink> {
  const { link } = await response;
  if (!link) throw new ProfileLinksApiError('bad_response', 200);
  return link;
}

/** The real client. `fetcher` is injectable for tests; the app uses `apiFetch`. */
export function createHttpProfileLinksApi(fetcher: Fetcher = apiFetch): ProfileLinksApi {
  return {
    list: () => call(fetcher, 'list', {}),
    get: (linkId) => linkOf(call(fetcher, 'get', { linkId })),
    createDraft: (input) => linkOf(call(fetcher, 'create-draft', { ...input })),
    update: (linkId, patch) => linkOf(call(fetcher, 'update', { linkId, patch })),
    publish: (linkId) => linkOf(call(fetcher, 'publish', { linkId })),
    pause: (linkId) => linkOf(call(fetcher, 'pause', { linkId })),
    archive: (linkId) => linkOf(call(fetcher, 'archive', { linkId })),
    stats: async (linkId) => {
      const { stats } = await call<{ stats?: ProfileLinkStats }>(fetcher, 'stats', { linkId });
      if (!stats) throw new ProfileLinksApiError('bad_response', 200);
      return stats;
    },
    leads: (linkId) => call(fetcher, 'leads', { linkId }),
    openProfileThread: async (handle, context) =>
      normalizeOpen(await call<Record<string, unknown>>(fetcher, 'open-profile-thread', context ? { handle, context } : { handle })),
    checkHandle: async (handle) => normalizeHandleCheck(await call<Record<string, unknown>>(fetcher, 'check-handle', { handle })),
    claimHandle: async (handle, language) =>
      normalizeClaim(await call<Record<string, unknown>>(fetcher, 'claim-handle', language ? { handle, language } : { handle })),
    prefillCard: async (linkId, language, options) => {
      const { link, prefill } = await call<Partial<PrefillCardResult>>(fetcher, 'prefill-card', {
        linkId,
        ...(language ? { language } : {}),
        ...(options?.auto ? { auto: true } : {}),
      });
      if (!link) throw new ProfileLinksApiError('bad_response', 200);
      return { link, prefill: prefill ?? { outcome: 'unavailable', filledFields: [], sources: [] } };
    },
  };
}

export { ProfileLinksApiError, isNotImplemented } from './profile-links-error';

export const profileLinksApi: ProfileLinksApi = createHttpProfileLinksApi();
